import React from "react";
import { Printer, AlertTriangle, Truck } from "lucide-react";

const ITEMS = [
  {
    icon: Printer,
    title: "Stuck with black-and-white printers",
    body: "Color, scale, and impact are off the table for everyday school print.",
  },
  {
    icon: AlertTriangle,
    title: "Broken or unreliable poster makers",
    body: "Aging hardware that jams, drifts, or sits idle when you need it most.",
  },
  {
    icon: Truck,
    title: "Reliant on outside vendors for everything",
    body: "Every banner, every sign, every event request goes through someone else.",
  },
];

const CONTEXT_IMG =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/a7p1pl3p_Students-Using-Posters-Created-with-a-School-Poster-Maker.webp";

export default function PrintersMirror() {
  return (
    <section
      data-testid="printers-mirror-section"
      className="relative bg-[#F4F6F4] py-24 md:py-32 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-14">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
              What Most Schools Are Dealing With
            </div>
            <h2
              data-testid="printers-mirror-headline"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
            >
              Most Schools Are Still Struggling With Printing
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Most schools:
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-md border border-slate-200">
              <img
                src={CONTEXT_IMG}
                alt="Teacher in a classroom — schools deserve better printing"
                loading="lazy"
                decoding="async"
                className="w-full h-[260px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold text-white/80 mb-1">
                  Reality on most campuses
                </span>
                <p className="font-serif text-white text-base sm:text-lg leading-snug">
                  The print queue is somebody else's problem—until it becomes yours.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <div
                key={it.title}
                className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-teal/40 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-teal/10 text-teal flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <h3 className="font-serif text-lg md:text-xl font-bold text-navy mb-2 leading-snug">
                  {it.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{it.body}</p>
              </div>
            );
          })}
        </div>

        <p
          data-testid="printers-mirror-close"
          className="mt-14 max-w-3xl font-serif text-2xl sm:text-3xl font-bold text-navy italic"
        >
          That's why everything takes longer—and costs more than it should.
        </p>
      </div>
    </section>
  );
}
